import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchJSON } from '../../utils/api';

export default function NewsletterPreferences() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => String(searchParams.get('token') || '').trim(), [searchParams]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [deleted, setDeleted] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    newsletterOptIn: true,
    category: 'Fans',
  });

  useEffect(() => {
    let canceled = false;

    async function load() {
      if (!token) {
        setError('Lenken mangler token.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setMessage('');
      try {
        const res = await fetchJSON(`/api/newsletter/manage?token=${encodeURIComponent(token)}`);
        if (canceled) return;
        const subscriber = res?.subscriber || {};
        setForm({
          firstName: subscriber.firstName || '',
          lastName: subscriber.lastName || '',
          email: subscriber.email || '',
          newsletterOptIn: !!subscriber.newsletterOptIn,
          category: subscriber.category || 'Fans',
        });
      } catch {
        if (canceled) return;
        setError('Kunne ikke åpne preferansesiden. Lenken kan være utløpt eller ugyldig.');
      } finally {
        if (!canceled) setLoading(false);
      }
    }

    load();
    return () => {
      canceled = true;
    };
  }, [token]);

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await fetchJSON('/api/newsletter/manage', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          newsletterOptIn: form.newsletterOptIn,
          category: form.category,
        }),
      });
      setMessage('Informasjonen er oppdatert.');
    } catch {
      setError('Kunne ikke lagre endringer.');
    } finally {
      setSaving(false);
    }
  };

  const unsubscribe = async () => {
    if (!token) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await fetchJSON('/api/newsletter/manage', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newsletterOptIn: false }),
      });
      setForm((prev) => ({ ...prev, newsletterOptIn: false }));
      setMessage('Du er avmeldt nyhetsbrevet.');
    } catch {
      setError('Kunne ikke melde av nyhetsbrevet.');
    } finally {
      setSaving(false);
    }
  };

  const removeData = async () => {
    if (!token) return;
    const confirmed = window.confirm('Slette all lagret informasjon for denne e-posten?');
    if (!confirmed) return;

    setDeleting(true);
    setError('');
    setMessage('');
    try {
      await fetchJSON('/api/newsletter/manage', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      setDeleted(true);
      setMessage('Informasjonen er slettet.');
    } catch {
      setError('Kunne ikke slette informasjonen.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000', padding: '96px 16px 32px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 'min(960px, 100%)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 14, padding: 16, background: 'rgba(0,0,0,0.45)', color: '#fff' }}>
        <h2 style={{ marginTop: 0 }}>Nyhetsbrev preferanser</h2>

        {loading ? <p>Laster…</p> : null}
        {!loading && error ? <p style={{ color: '#ff9b9b' }}>{error}</p> : null}
        {!loading && message ? <p style={{ color: '#9bffb1' }}>{message}</p> : null}

        {!loading && !error && !deleted ? (
          <form onSubmit={save} style={{ display: 'grid', gap: 10 }}>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              placeholder="Fornavn"
              style={{ padding: '9px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.22)', background: 'transparent', color: 'inherit' }}
            />
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              placeholder="Etternavn"
              style={{ padding: '9px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.22)', background: 'transparent', color: 'inherit' }}
            />
            <input
              type="email"
              value={form.email}
              onChange={(e) => onChange('email', e.target.value)}
              placeholder="E-post"
              required
              style={{ padding: '9px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.22)', background: 'transparent', color: 'inherit' }}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={!!form.newsletterOptIn}
                onChange={(e) => onChange('newsletterOptIn', e.target.checked)}
              />
              Motta nyhetsbrev
            </label>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="submit"
                disabled={saving || deleting}
                style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: '#6c47ff', color: '#fff', cursor: saving || deleting ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Lagrer…' : 'Lagre'}
              </button>
              <button
                type="button"
                onClick={unsubscribe}
                disabled={saving || deleting}
                style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.25)', background: 'transparent', color: '#fff', cursor: saving || deleting ? 'not-allowed' : 'pointer' }}
              >
                Meld av nyhetsbrev
              </button>
              <button
                type="button"
                onClick={removeData}
                disabled={saving || deleting}
                style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,120,120,0.45)', background: 'rgba(180,20,20,0.15)', color: '#ffd5d5', cursor: saving || deleting ? 'not-allowed' : 'pointer' }}
              >
                {deleting ? 'Sletter…' : 'Slett informasjon'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}
